/**
 * Crop geometry for the aspect check.
 *
 * MOVED, NOT RE-DERIVED. The bodies below are verbatim copies of
 * `src/components/ui/inspector/bodies/post/cropSignal.ts` (thresholds,
 * `parseAspectValue`, `edgeCropFraction`). That file cannot be imported here
 * because its line 1 uses the `@/` alias, which Deno cannot resolve through
 * the edge stub — but the VALUES are the already-reviewed, already-shipped
 * choice, and changing them silently would change the composer's existing
 * crop warning. The orchestrator patch in this lane's report reduces
 * cropSignal.ts to a re-export of this module so the two copies cannot drift.
 *
 * The metric is the single-axis EDGE-crop fraction (how much of the long edge
 * the platform trims) — what a human perceives as "how much got cut" — not
 * area loss, which explodes to 40–60% on routine conversions.
 */

/** Below this the crop is negligible — no warning. */
export const MINOR_CROP_THRESHOLD = 0.08;
/** At/above this the crop is severe — stronger emphasis. */
export const MAJOR_CROP_THRESHOLD = 0.25;

/** Parse a persisted aspect string ('16:9', '1.78:1', or '1.78') to W/H. */
export function parseAspectValue(value: string | null | undefined): number | null {
  if (!value) return null;
  const v = value.trim();
  if (v.includes(':')) {
    const [w, h] = v.split(':').map(Number);
    return Number.isFinite(w) && Number.isFinite(h) && h !== 0 ? w / h : null;
  }
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Edge-crop fraction when `sourceRatio` is clamped into `[acceptMin, acceptMax]`
 * (what the platform does to fit). Returns 0 when the source is already inside
 * the window. The axis is the pair of edges trimmed.
 */
export function edgeCropFraction(
  sourceRatio: number,
  acceptMin: number,
  acceptMax: number,
): { fraction: number; axis: 'sides' | 'top & bottom' } {
  const target = Math.min(Math.max(sourceRatio, acceptMin), acceptMax);
  if (target === sourceRatio) return { fraction: 0, axis: 'sides' };
  // Source wider than the window → the platform trims the SIDES; taller → top & bottom.
  if (sourceRatio > target) return { fraction: 1 - target / sourceRatio, axis: 'sides' };
  return { fraction: 1 - sourceRatio / target, axis: 'top & bottom' };
}
